import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import track from 'react-tracking';
import { DocumentNode } from 'graphql';
import { withUserContext, IUserContext } from './user_context_provider';
import { isExperimentEnabled } from '../user_context_helpers';
import experiments from '../experiments';
import { userIsTrackable } from '../elog/mparticle';
import ListingsCollection, { ListingsCollection as ListingsCollectionClass } from './listings_collection';
import { mParticleListingFields } from './with_mparticle_listings';
import { CommonsWatchedListings } from '../rql';
import { withGraphql } from '../with_graphql';
import { connect } from '../connect';
import { WatchedListingsFallback } from './watched_listings_fallback';

const COMPONENT_NAME = 'WatchRow';
const MAX_COUNT = 12;
const MIN_COUNT = 4;
const EXPERIMENTAL_MIN_COUNT = 5;
const EXPERIMENTAL_MAX_COUNT = 8;

export interface ExternalProps {
  mainCallToAction?: React.ReactElement;
  secondaryCallToAction?: React.ReactElement;
  newHomepageDesign?: boolean;
}

type IProps = ChildProps<IUserContext & ExternalProps, CommonsWatchedListings.Query>;

export function WatchedListings(props: IProps) {
  const { data: { me, loading } } = props;
  const limit = props.newHomepageDesign ? EXPERIMENTAL_MAX_COUNT : MAX_COUNT;
  const listings = (me?.watches?.nodes ?? []).map(n => n.listing).slice(0, limit);
  const isEmptyWatchlist = !loading && !listings.length;
  const title = I18n.t('commons.watchRow.loggedIn.title');
  const minCount = props.newHomepageDesign ? EXPERIMENTAL_MIN_COUNT : MIN_COUNT;

  if (listings.length < minCount && !loading) {
    const topProductTypeUuids = me?.profile?.topProductTypeUuids || [];
    const topProductTypes = me?.profile?.topProductTypes || [];

    return (
      <WatchedListingsFallback
        emptyStateMainCallToAction={props.mainCallToAction}
        emptyStateSecondaryCallToAction={props.secondaryCallToAction}
        hasWatchedListings={!isEmptyWatchlist}
        topProductTypeUuid={topProductTypeUuids[0]}
        topProductTypeName={topProductTypes[0]}
      />
    );
  }

  return (
    <ListingsCollection
      listings={listings}
      loading={loading}
      callToAction={props.mainCallToAction}
      componentName={COMPONENT_NAME}
      title={title}
      indentedMosaic={props.newHomepageDesign}
      largeTiles={!props.newHomepageDesign}
    />
  );
}

function buildWatchedListingsQuery(imagesQuery: DocumentNode) {
  return gql`
    query Commons_WatchedListings(
      $limit: Int
      $showListingLocation: Boolean!
      $shouldSkipTracking: Boolean!
    ) {
      me {
        uuid
        profile {
          topProductTypeUuids
          topProductTypes
        }
        watches(input: {
          limit: $limit
          listingsState: LIVE
        }) {
          nodes {
            _id
            listing {
              _id
              ... ListingsCollection
              ... mParticleListingFields @skip(if: $shouldSkipTracking)
              ... ShopFields @include(if: $showListingLocation)
            }
          }
        }
      }
    }
    ${imagesQuery}
    ${ListingsCollectionClass.fragments.shopFields}
    ${mParticleListingFields}
  `;
}

function buildWatchedListingsVars(ownProps: IProps) {
  return {
    ssr: true,
    skip: !!ownProps.user?.loggedOut,
    variables: {
      limit: MAX_COUNT,
      showListingLocation: isExperimentEnabled(ownProps.user, experiments.LISTING_CARD_LOCATION),
      shouldSkipTracking: !userIsTrackable(),
    },
  };
}

const queryDefault = withGraphql<IProps, CommonsWatchedListings.Query, CommonsWatchedListings.Variables>(
  buildWatchedListingsQuery(ListingsCollectionClass.fragments.listing),
  {
    options: buildWatchedListingsVars,
  },
);

const queryLarge = withGraphql<IProps, CommonsWatchedListings.Query, CommonsWatchedListings.Variables>(
  buildWatchedListingsQuery(ListingsCollectionClass.fragments.listingLarge),
  {
    options: buildWatchedListingsVars,
  },
);

const WatchedListingsDefault = connect<ExternalProps>([
  withUserContext,
  queryDefault,
])(track({ componentName: COMPONENT_NAME })(WatchedListings));

const WatchedListingsLarge = connect<ExternalProps>([
  withUserContext,
  queryLarge,
])(track({ componentName: COMPONENT_NAME })(WatchedListings));

export default (props: IProps) => {
  if (props.newHomepageDesign) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WatchedListingsLarge {...props} />;
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <WatchedListingsDefault {...props} />;
};
